import { createSystem, defaultConfig, defineConfig, defineSlotRecipe } from '@chakra-ui/react';
import { defineRecipe } from '@chakra-ui/react';

export const accordionRecipe = defineSlotRecipe({
  className: 'accordion',
  slots: ['itemTrigger', 'itemContent'],

  base: {
    itemTrigger: {
      // padding: '4',
      backgroundColor: 'background',
      padding: 0,
      cursor: 'pointer',
      _hover: { backgroundColor: 'logoPurple' },
      _expanded: { bg: 'backgroundSecondary' },
    },
    itemContent: {
      backgroundColor: 'backgroundSecondary',
      padding: 0,
      // padding: '0 4 4 4',
    },
  },
});

const buttonRecipe = defineRecipe({
  base: {
    display: 'flex',
    _hover: { bg: 'logoPurple' },
  },
  variants: {
    variant: {
      solid: { bg: 'logoPurple', color: 'textOnDark', _hover: { bg: 'logoPurple' } },
      outline: { borderWidth: '1px', borderColor: 'logoPurple', _hover: { bg: 'logoPurple' } },
    },
    size: {
      sm: { padding: '4', fontSize: '12px' },
      lg: { padding: '8', fontSize: '24px' },
    },
  },
});

export const checkboxRecipe = defineSlotRecipe({
  className: 'checkbox',
  slots: ['root', 'control', 'label'],
  base: {
    root: {
      bg: 'logoPurple.400',
      _hover: {
        '& .checkbox__label': {
          color: 'textOnLight',
        },
      },
    },
    control: {
      backgroundColor: 'background',
      color: 'logoPurple',
      _checked: { bg: 'logoPurple', color: 'textOnDark', borderColor: 'logoPurple' },
      // ...
    },
    label: {
      // ...
    },
  },
});

const spinnerRecipe = defineRecipe({
  base: {
    color: 'logoPurple',
  },
});

export const selectRecipe = defineSlotRecipe({
  slots: ['root', 'control', 'trigger'],
  base: {
    root: {},
    control: {},
    trigger: {
      border: 'none',
    },
  },
});

const recipes = {
  button: buttonRecipe,
  spinner: spinnerRecipe,
}

type defaultThemeFields = {
  // surface
  background: { value: string };
  navBG: { value: string };

  midCardBG: { value: string };
  cardDefault: { value: string };
  cardHover: { value: string };
  cardActive: { value: string };

  // interaction
  navButtonDefault: { value: string };
  navButtonActive: { value: string };
  navButtonHover: { value: string };

  accentButtonDefault: { value: string };
  accentButtonActive: { value: string };
  accentButtonHover: { value: string };

  // text
  primaryText: { value: string };
  secondaryText: { value: string };

  // error
  errorBG: { value: string };
  errorText: { value: string };
  errorAccent: { value: string };

  // other
  logoPurple: { value: string };
  whiteIcon: { value: string };
  dropShadow: { value: string };
  accentRed: { value: string };
  accentYellow: { value: string };
  accentBlue: { value: string };

  // // outdated
  // primary: { value: string };
  // ai: { value: string };
  // backgroundSecondary: { value: string };
  // textOnLight: { value: string };
  // textOnDark: { value: string };
  // dataset: { value: string };
};

const lightModeColors: defaultThemeFields = {
  logoPurple:           { value: '#b794f4ff' },
  background:           { value: '#ebe5f5ff' },
  accentButtonDefault:  { value: '#736bebff' },
  accentButtonActive:   { value: '#6A63CCff' },
  accentButtonHover:    { value: '#817AEDff' }, 
  navBG:                { value: '#736bebff' },
  navButtonDefault:     { value: '#837cedff' },
  navButtonActive:      { value: '#c7c4ffff' },
  navButtonHover:       { value: '#9e98fbff' },
  whiteIcon:            { value: '#ffffffff' },
  cardDefault:          { value: '#ffffffff' },
  cardActive:           { value: '#e3dcedff' },
  cardHover:            { value: '#d2c2e8ff' },
  midCardBG:            { value: '#f9f5ffff' },
  dropShadow:           { value: '#ded3efff' },
  primaryText:          { value: '#3b374dff' },
  secondaryText:        { value: '#938ab6ff' },
  errorBG:              { value: '#eac3c3ff' },
  errorAccent:          { value: '#b77171ff' },
  errorText:            { value: '#b77171ff' },
  accentRed:            { value: '#ed435aff' },
  accentYellow:         { value: '#f9ca25ff' },
  accentBlue:           { value: '#0286d5ff' },
}

const darkModeColors: defaultThemeFields = {
  logoPurple:           { value: '#b794f4ff' },
  background:           { value: '#2f363fff' },
  accentButtonDefault:  { value: '#736bebff' },
  accentButtonActive:   { value: '#6A63CCff' },
  accentButtonHover:    { value: '#817AEDff' }, 
  navBG:                { value: '#1e262aff' },
  navButtonDefault:     { value: '#3b4245ff' },
  navButtonActive:      { value: '#6b6f76ff' },
  navButtonHover:       { value: '#555c69ff' },
  whiteIcon:            { value: '#ffffffff' },
  cardDefault:          { value: '#181d23ff' },
  cardActive:           { value: '#2e343bff' },
  cardHover:            { value: '#252b32ff' },
  midCardBG:            { value: '#252b32ff' },
  dropShadow:           { value: '#27303bff' },
  primaryText:          { value: '#c8bef4ff' },
  secondaryText:        { value: '#938ab6ff' },
  errorBG:              { value: '#dc8484ff' },
  errorAccent:          { value: '#8a1c1cff' },
  errorText:            { value: '#8a1c1cff' },
  accentRed:            { value: '#ed435aff' },
  accentYellow:         { value: '#f9ca25ff' },
  accentBlue:           { value: '#0286d5ff' },
};

const ericModeColors: defaultThemeFields = {
  logoPurple:           { value: '#B794F4ff' },
  background:           { value: '#282C34ff' },
  accentButtonDefault:  { value: '#1f9c74ff' },
  accentButtonActive:   { value: '#337220ff' },
  accentButtonHover:    { value: '#185032ff' }, 
  navBG:                { value: '#21252Bff' },
  navButtonDefault:     { value: '#599c85ff' },
  navButtonActive:      { value: '#437966ff' },
  navButtonHover:       { value: '#356152ff' },
  whiteIcon:            { value: '#FFFFFFff' },
  cardDefault:          { value: '#15191dff' },
  cardActive:           { value: '#030d18ff' },
  cardHover:            { value: '#2b3239ff' },
  midCardBG:            { value: '#21252B' },
  dropShadow:           { value: '#27303bff' },
  primaryText:          { value: '#9a4e37ff' },
  secondaryText:        { value: '#297857ff' },
  errorBG:              { value: '#dc8484ff' },
  errorAccent:          { value: '#8A3131ff' },
  errorText:            { value: '#8A3131ff' },
  accentRed:            { value: '#BE3345ff' },
  accentYellow:         { value: '#C09D22ff' },
  accentBlue:           { value: '#099383ff' },
}

const festiveModeColors: defaultThemeFields = {
  logoPurple:           { value: '#f4a194ff' },
  background:           { value: '#f0f4efff' },
  accentButtonDefault:  { value: '#5b8757ff' },
  accentButtonActive:   { value: '#8bac85ff' },
  accentButtonHover:    { value: '#76a776ff' }, 
  navBG:                { value: '#d47777ff' },
  navButtonDefault:     { value: '#5f8c69ff' },
  navButtonActive:      { value: '#96b296ff' },
  navButtonHover:       { value: '#7cb278ff' },
  whiteIcon:            { value: '#ffffffff' },
  cardDefault:          { value: '#d3dfd2ff' },
  cardActive:           { value: '#dceddfff' },
  cardHover:            { value: '#e4eee2ff' },
  midCardBG:            { value: '#e3ebe1ff' },
  dropShadow:           { value: '#d3efd3ff' },
  primaryText:          { value: '#3b374dff' },
  secondaryText:        { value: '#66846Fff' },
  errorBG:              { value: '#eac3c3ff' },
  errorAccent:          { value: '#b77171ff' },
  errorText:            { value: '#b77171ff' },
  accentRed:            { value: '#ed435aff' },
  accentYellow:         { value: '#f9ca25ff' },
  accentBlue:           { value: '#0286d5ff' },
}

const themeDarkmode = defineConfig({
  theme: {
    tokens: {
      colors: darkModeColors
    },
    recipes: recipes
  },
})

const themeLightMode = defineConfig({
  theme: {
    tokens: {
      colors: lightModeColors
    },
    recipes: recipes
  },
})

const themeEricMode = defineConfig({
  theme: {
    tokens: {
      colors: ericModeColors
    },
    recipes: recipes
  },
})

const themeFestiveMode = defineConfig({
  theme: {
    tokens: {
      colors: festiveModeColors,
    },
    recipes: recipes,
  },
});


export const systemLightMode = createSystem(defaultConfig, themeLightMode);
const systemDarkMode = createSystem(defaultConfig, themeDarkmode);
const systemEricMode = createSystem(defaultConfig, themeEricMode);
const systemFestiveMode = createSystem(defaultConfig, themeFestiveMode);

export const systemModes = {
  light: systemLightMode,
  dark: systemDarkMode,
  eric: systemEricMode,
  festive: systemFestiveMode,
} as const;

export type SystemModeType = keyof typeof systemModes;
export type AppSystemColorType = (typeof systemModes)[SystemModeType]

